import { OperationType } from 'features/analysis/types'
import { ProviderType } from 'features/connections'
import { DATA_TYPES } from 'type-analyzer'
import { getUniqueColumnName } from '../../ColumnsFromSource/queries/utils'
import { IntersectAndAggregateQuery, IntersectAndAggregateSettingsQueryParams } from '../types'
import { generateQuery as generateQueryForBigQuery } from './bq'
import { generateQuery as generateQueryForSnowflake } from './sf'
import { generateQuery as generateQueryForRedshift } from './rs'
import { generateQuery as generateQueryForPostgres } from './pg'
import { ANALYSIS_FIELDS_DENYLIST } from 'features/builder/ui/builderConstants'

export function generateIntersectAndAggregateQuery({
  sourceDataset,
  secondDataset,
  operation,
  secondSourceColumn
}: IntersectAndAggregateQuery): string {
  const operationQuery = generateOperation(operation, secondSourceColumn)
  const resultColumnName = generateResultColumnName(operation, secondSourceColumn)
  const uniqueColumnName = getUniqueColumnName({
    columnName: resultColumnName,
    columns: sourceDataset.metadata?.schema.map((c) => c.name)
  })
  const baseGeometryColumn =
    sourceDataset.geoColumn || sourceDataset.metadata?.schema?.find((c) => c.type === 'geometry')?.name || 'geom'
  const secondGeometryColumn =
    secondDataset.geoColumn || secondDataset.metadata?.schema?.find((c) => c.type === 'geometry')?.name || 'geom'

  // throw error if no geometry column
  if (!baseGeometryColumn || !secondGeometryColumn) {
    throw new Error('No geometry column found')
  }

  const excludedColumnsTypes = [DATA_TYPES.GEOMETRY, DATA_TYPES.OBJECT]

  const columns = sourceDataset.metadata.schema
    .filter(
      (field) => !excludedColumnsTypes.includes(field.type?.toUpperCase()) && !ANALYSIS_FIELDS_DENYLIST.has(field.name)
    )
    .map((field) => `base.${field.name}`)

  const payload: IntersectAndAggregateSettingsQueryParams = {
    columns,
    baseGeometryColumn,
    secondGeometryColumn,
    operation: operationQuery,
    base: sourceDataset,
    second: secondDataset,
    resultColumnName: uniqueColumnName
  }

  const connectionProvider = sourceDataset.providerId

  if (operation !== OperationType.COUNT && !secondSourceColumn) {
    return ''
  }

  switch (connectionProvider) {
    case ProviderType.BIGQUERY:
      return generateQueryForBigQuery(payload)
    case ProviderType.SNOWFLAKE:
      return generateQueryForSnowflake(payload)
    case ProviderType.REDSHIFT:
      return generateQueryForRedshift(payload)
    case ProviderType.POSTGRES:
      return generateQueryForPostgres(payload)
    default:
      throw new Error(`Analysis not implemented for ${connectionProvider}`)
  }
}

function generateOperation(operation: OperationType, secondSourceColumn?: string) {
  switch (operation) {
    case OperationType.COUNT:
      return 'COUNT(*)'
    case OperationType.AVG:
    case OperationType.SUM:
    case OperationType.MAX:
    case OperationType.MIN:
      return `${operation.toUpperCase()}(second.${secondSourceColumn})`
  }
}

function generateResultColumnName(operation: OperationType, secondSourceColumn?: string) {
  switch (operation) {
    case OperationType.COUNT:
      return operation
    case OperationType.AVG:
    case OperationType.SUM:
    case OperationType.MAX:
    case OperationType.MIN:
      return `${operation}_${secondSourceColumn}`
  }
}
